import { client } from './client'
import { acquireAccessTokenOrRedirect } from '@/auth/authSession'
import { getSelectedTenantId } from '@/lib/tenantStorage'
import type { Chat, ChatAgentOptions, ChatAgentSelection, ChatContainerRequirement, ChatMessage, ChatSessionStatus, CreateChatResponse } from '@/types'

export const chatsApi = {
  getAll: (projectId: string, search?: string) =>
    client.get<Chat[]>(`/projects/${projectId}/chats`, { params: { search: search || undefined } }).then((r) => r.data),

  getAgentOptions: (projectId: string) =>
    client.get<ChatAgentOptions>(`/projects/${projectId}/chats/agent-options`).then((r) => r.data),

  updateAgentSelection: (projectId: string, chatId: string, selection: ChatAgentSelection) =>
    client.put(`/projects/${projectId}/chats/${chatId}/agent-selection`, selection).then((r) => r.data),

  create: (projectId: string) =>
    client.post<CreateChatResponse>(`/projects/${projectId}/chats`).then((r) => r.data),

  delete: (projectId: string, chatId: string) =>
    client.delete(`/projects/${projectId}/chats/${chatId}`).then((r) => r.data),

  updateTitle: (projectId: string, chatId: string, title: string) =>
    client.put(`/projects/${projectId}/chats/${chatId}/title`, { title }).then((r) => r.data),

  getMessages: (projectId: string, chatId: string) =>
    client.get<ChatMessage[]>(`/projects/${projectId}/chats/${chatId}/messages`).then((r) => r.data),

  getContainerRequirement: (projectId: string, chatId: string) =>
    client
      .get<ChatContainerRequirement>(`/projects/${projectId}/chats/${chatId}/container-requirement`)
      .then((r) => r.data),

  getLatestSession: (projectId: string, chatId: string) =>
    client
      .get<ChatSessionStatus | null>(`/projects/${projectId}/chats/${chatId}/session`)
      .then((r) => r.data),

  cancelSession: (projectId: string, chatId: string, sessionId: string) =>
    client
      .post(`/projects/${projectId}/chats/${chatId}/sessions/${sessionId}/cancel`)
      .then((r) => r.data),

  getRuns: (projectId: string, chatId: string, status?: string) =>
    client
      .get<WorkflowRunSummary[]>(`/projects/${projectId}/chats/${chatId}/runs`, {
        params: { status: status || undefined },
      })
      .then((r) => r.data),
}

export interface WorkflowRunSummary {
  runId: string
  workflowDefinitionId: string
  workflowDefinitionName: string
  definitionVersion: string
  status: string
  startedAt: string | null
  completedAt: string | null
  triggeredBy: string | null
  triggeredByName: string | null
  isTerminal: boolean
}

export async function streamMessage(
  projectId: string,
  chatId: string,
  message: string,
  onEvent: (event: string, data: string) => void,
  signal?: AbortSignal,
  attachmentIds?: string[],
  useContainer = true,
  model?: string,
  effort?: string,
  skillIds?: string[],
  agentProvider?: string,
) {
  const token = await acquireAccessTokenOrRedirect()
  if (!token) return

  const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api'
  const url = `${baseUrl}/projects/${projectId}/chats/${chatId}/messages`

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }

  const tenantId = getSelectedTenantId()
  if (tenantId) {
    headers['X-Tenant-Id'] = tenantId
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ message, useContainer, ...(attachmentIds?.length ? { attachmentIds } : {}), ...(model ? { model } : {}), ...(effort ? { effort } : {}), ...(skillIds?.length ? { skillIds } : {}), ...(agentProvider ? { agentProvider } : {}) }),
    signal,
  })

  if (!response.ok) {
    const body = await response.json().catch(() => undefined)
    throw new Error(body?.message ?? `Stream request failed: ${response.status}`)
  }

  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('No response body')
  }

  const decoder = new TextDecoder()
  let buffer = ''
  // A network read can end between an `event:` line and its `data:` line, so the name must outlive one read.
  let currentEvent = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })

    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim()
      } else if (line.startsWith('data: ') && currentEvent) {
        const data = line.slice(6).trim()
        onEvent(currentEvent, data)
        currentEvent = ''
      }
    }
  }
}
